import { useAuth } from "@features/auth/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { AlertCircle, CreditCard, Clock, XCircle } from "lucide-react";

/**
 * Banking Info Banner Component
 * Displays alerts to guide users through banking verification process
 * Shows different states: missing, pending, rejected, or verified
 */
export default function BankingInfoBanner() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Skip banner if user is not a client
  if (user?.role !== "client") {
    return null;
  }

  const hasBankingInfo =
    user?.bankAccount?.bankName && user?.bankAccount?.bankNumber;
  const hasCNI = user?.cni?.number;
  const isBankingVerified =
    user?.bankAccount?.verified || user?.bankingStatus === "approved";
  const isBankingPending = user?.bankingStatus === "pending";
  const isBankingRejected = user?.bankingStatus === "rejected";
  const bankingRejectionReason = user?.bankingRejectionReason;

  // Don't show banner if everything is verified
  if (hasBankingInfo && hasCNI && isBankingVerified) {
    return null;
  }

  // Banking rejected state
  if (isBankingRejected) {
    return (
      <Alert
        variant="destructive"
        className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20"
      >
        <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
        <AlertTitle className="text-red-900 dark:text-red-100 font-semibold">
          {t("banking.bankingRejected") || "Banking Information Rejected"}
        </AlertTitle>
        <AlertDescription className="text-red-800 dark:text-red-200">
          <p className="mb-2">
            {t("banking.bankingRejectedDescription") ||
              "Your banking information was rejected. Please review and resubmit."}
          </p>
          {bankingRejectionReason && (
            <p className="mb-3 p-2 bg-red-100 dark:bg-red-900/30 rounded border border-red-200 dark:border-red-800 text-sm">
              <strong>{t("banking.reason")}:</strong> {bankingRejectionReason}
            </p>
          )}
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate("/setup-account")}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            {t("banking.resubmitBanking") || "Resubmit Banking Info"}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Banking pending approval state
  if (isBankingPending && hasBankingInfo) {
    return (
      <Alert className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/20">
        <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
        <AlertTitle className="text-yellow-900 dark:text-yellow-100 font-semibold">
          {t("banking.bankingPending") || "Banking Information Under Review"}
        </AlertTitle>
        <AlertDescription className="text-yellow-800 dark:text-yellow-200">
          <p className="mb-2">
            {t("banking.bankingPendingDescription") ||
              "Your banking information is being reviewed by our team. You'll be notified once it's approved."}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/setup-account")}
            className="border-yellow-600 text-yellow-700 hover:bg-yellow-100 dark:border-yellow-500 dark:text-yellow-300"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            {t("common.viewDetails") || "View Details"}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Missing banking information (priority alert)
  if (!hasBankingInfo || !hasCNI) {
    const missingItems = [];
    if (!hasBankingInfo)
      missingItems.push(t("banking.bankAccount") || "Bank Account");
    if (!hasCNI) missingItems.push(t("banking.cniInfo") || "CNI (ID Card)");

    return (
      <Alert
        variant="default"
        className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20"
      >
        <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        <AlertTitle className="text-blue-900 dark:text-blue-100 font-semibold">
          {t("banking.completeBankingInfo") ||
            "Complete Your Banking Information"}
        </AlertTitle>
        <AlertDescription className="text-blue-800 dark:text-blue-200">
          <p className="mb-2">
            {t("banking.bankingRequiredForPayments") ||
              "Banking information is required to receive payments for your deliveries."}
          </p>
          <p className="mb-3 text-sm">
            <strong>{t("common.missing")}:</strong> {missingItems.join(", ")}
          </p>
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate("/setup-account")}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            {t("banking.setupBanking") || "Setup Banking Info"}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Banking submitted but not yet verified (waiting state)
  if (hasBankingInfo && !isBankingVerified && !isBankingPending) {
    return (
      <Alert className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/20">
        <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
        <AlertTitle className="text-orange-900 dark:text-orange-100 font-semibold">
          {t("banking.bankingNotVerified") ||
            "Banking Information Not Verified"}
        </AlertTitle>
        <AlertDescription className="text-orange-800 dark:text-orange-200">
          <p className="mb-2">
            {t("banking.bankingNotVerifiedDescription") ||
              "Your banking information has been submitted but is not yet verified."}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/setup-account")}
            className="border-orange-600 text-orange-700 hover:bg-orange-100 dark:border-orange-500 dark:text-orange-300"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            {t("common.viewDetails") || "View Details"}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}
